import { useState } from "react";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  styled,
} from "@mui/material";
import { Dialog, DialogContent, DialogTitle } from "@components/shared/DialogCompat";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const TrialChip = styled(Box, { shouldForwardProp: (prop) => prop !== "warning" })(
  ({ theme, warning }) => ({
    display: "flex",
    height: "fit-content",
    backgroundColor: warning ? theme.palette.secondary.main : "white",
    color: warning ? theme.palette.secondary.contrastText : theme.palette.grey[900],
    padding: "0.6rem",
    borderRadius: "14px",
    fontSize: "0.75rem",
    alignItems: "center",
    marginRight: "0.5rem",
  })
);

const HelpButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== "warning" })(
  ({ theme, warning }) => ({
    padding: 0,
    marginLeft: "0.4rem",
    color: warning ? theme.palette.secondary.contrastText : theme.palette.grey[900],
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  })
);

const COSTS = [
  { action: "Create a Float", cost: 1 },
  { action: "Create a Candidate", cost: 1 },
  { action: "Create a Submission", cost: 1 },
  { action: "Create a Job", cost: 1 },
];

export default function TrialNotice({ credits }) {
  const [open, setOpen] = useState(false);
  const remaining = Number.isFinite(credits) ? credits : 0;
  const warning = remaining < 5;

  return (
    <>
      <TrialChip warning={warning}>
        <strong>Free&nbsp;</strong>
        <Box
          component="img"
          src="/img/gold-coin.png"
          alt=""
          sx={{ width: "1rem", height: "1rem", mr: 0.5 }}
        />
        <span>
          {remaining} credit{remaining === 1 ? "" : "s"} left
        </span>
        <HelpButton
          warning={warning}
          aria-label="How do credits work?"
          onClick={() => setOpen(true)}
        >
          <HelpOutlineIcon />
        </HelpButton>
      </TrialChip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1.5rem" }}>
          How credits work
          <IconButton aria-label="Close" onClick={() => setOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Your free plan gives you a set number of credits to try out Floats. Each
            of the actions below costs one credit. Editing existing items is
            free.
          </Typography>
          <List dense disablePadding>
            {COSTS.map(({ action, cost }) => (
              <ListItem key={action} disableGutters sx={{ py: 0.5 }}>
                <ListItemText
                  primary={action}
                  secondary={`${cost} credit${cost === 1 ? "" : "s"}`}
                  primaryTypographyProps={{ variant: "body2" }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItem>
            ))}
          </List>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
            When you run out of credits, upgrade to a paid plan to keep going.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
